import React, { useState } from "react"
import { Form } from "react-bootstrap"
import { uploadFilesAWS } from "../../../api/Webservices"
import { showToast } from "../../Shared/ToastMessage"

export function FormField({ field, value, onChange, error }) {
  const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/jpg"]
  const MAX_IMAGE_SIZE = 5 * 1024 * 1024 // 5MB
  const [previewUrls, setPreviewUrls] = useState(null) // Store preview URLs locally

  const validateImage = (file) => {
    // Check file type
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      return `File must be JPG, JPEG, or PNG format.`
    }
    // Check file size
    if (file.size > MAX_IMAGE_SIZE) {
      return `File size must not exceed 5MB.`
    }
    // Check image dimensions (optional, omitted since your example doesn't enforce specific dimensions)
    return new Promise((resolve) => {
      const img = new Image()
      const objectUrl = URL.createObjectURL(file)
      img.onload = () => {
        URL.revokeObjectURL(objectUrl)
        resolve(null)
      }
      img.onerror = () => {
        URL.revokeObjectURL(objectUrl)
        resolve(`Failed to load file. Please try again.`)
      }
      img.src = objectUrl
    })
  }

  const handleChange = async (e) => {
    const { type, checked, value: inputValue, files } = e.target

    if (type === "file") {
      const file = field.multiple ? files : files[0]
      if (!file) {
        onChange(field.multiple ? [] : null)
        setPreviewUrls(null)
        return
      }

      // Validate each file
      const filesToValidate = field.multiple ? Array.from(files) : [file]
      for (const f of filesToValidate) {
        const validationError = await validateImage(f)
        if (validationError) {
          showToast(validationError, "error")
          return
        }
      }

      // If validation passes, upload files to AWS
      try {
        const res = await uploadFilesAWS({ upload_files: field.multiple ? files : file })
        if (res.data && res.data.success === true) {
          const uploadedUrls = field.multiple
            ? res.data.uploadedUrls.upload_files
            : res.data.uploadedUrls.upload_files[0]
          const newPreviewUrls = field.multiple
            ? Array.from(files).map((f) => URL.createObjectURL(f))
            : URL.createObjectURL(file)

          // Update parent with only the uploaded URL(s)
          onChange(uploadedUrls)
          // Store preview URLs locally for display
          setPreviewUrls(newPreviewUrls)
        } else {
          showToast(res.message, "error")
        }
      } catch (error) {
        console.error("Error uploading file:", error)
        showToast("Failed to upload file. Please try again.", "error")
      }
    } else if (type === "checkbox") {
      if (field.options) {
        // For checkbox groups, ensure value is an array
        const currentValue = Array.isArray(value) ? [...value] : []
        if (checked) {
          // Add the value if checked
          if (!currentValue.includes(inputValue)) {
            currentValue.push(inputValue)
          }
        } else {
          // Remove the value if unchecked
          const index = currentValue.indexOf(inputValue)
          if (index !== -1) {
            currentValue.splice(index, 1)
          }
        }
        console.log(`Checkbox group change: field=${field.id}, newValue=`, currentValue) // Debugging log
        onChange(currentValue)
      } else {
        // Single checkbox, use the checked state
        console.log(`Single checkbox change: field=${field.id}, checked=${checked}`) // Debugging log
        onChange(checked)
      }
    } else {
      onChange(inputValue)
    }
  }

  const renderField = () => {
    switch (field.type) {
      // Text inputs
      case "text":
      case "password":
      case "email":
      case "number":
      case "tel":
      case "url":
      case "search":
      case "date":
      case "time":
      case "datetime-local":
      case "month":
      case "week":
        return (
          <Form.Control
            type={field.type}
            placeholder={field.placeholder}
            value={value || ""}
            onChange={handleChange}
            isInvalid={!!error}
            required={field.required}
          />
        )

      // Special inputs
      case "color":
        return (
          <Form.Control
            type="color"
            value={value || "#000000"}
            onChange={handleChange}
            isInvalid={!!error}
            required={field.required}
          />
        )

      case "range":
        return (
          <>
            <Form.Control
              type="range"
              min={field.min || "0"}
              max={field.max || "100"}
              step={field.step || "1"}
              value={value || "0"}
              onChange={handleChange}
              isInvalid={!!error}
              required={field.required}
            />
            <small className="text-muted">Current value: {value || 0}</small>
          </>
        )

      case "file":
        return (
          <>
            <Form.Control
              type="file"
              onChange={handleChange}
              isInvalid={!!error}
              required={field.required}
              accept={field.accept || "image/jpeg,image/png,image/jpg"}
              multiple={field.multiple}
            />
            {field.multiple && value && (
              <small className="text-muted">
                {value.length} file{value.length !== 1 ? "s" : ""} selected
              </small>
            )}
            {previewUrls && (
              <div className="mt-2">
                {field.multiple ? (
                  previewUrls.map((previewUrl, index) => (
                    <img
                      key={index}
                      src={previewUrl}
                      alt={`Preview ${index + 1}`}
                      style={{ maxWidth: "100px", marginRight: "10px", marginTop: "5px" }}
                    />
                  ))
                ) : (
                  <img src={previewUrls} alt="Preview" style={{ maxWidth: "100px", marginTop: "5px" }} />
                )}
              </div>
            )}
          </>
        )

      case "hidden":
        return <Form.Control type="hidden" value={value || ""} onChange={handleChange} />

      case "textarea":
        return (
          <Form.Control
            as="textarea"
            rows={field.rows || 3}
            placeholder={field.placeholder}
            value={value || ""}
            onChange={handleChange}
            isInvalid={!!error}
            required={field.required}
          />
        )

      case "select":
        return (
          <Form.Control
            as="select"
            value={value || ""}
            onChange={handleChange}
            isInvalid={!!error}
            required={field.required}
          >
            <option value="">{field.placeholder || "Select an option"}</option>
            {field.options &&
              field.options.map((option, index) => {
                const optionLabel = typeof option === "object" ? option.label : option
                const optionValue = typeof option === "object" ? option.value : option
                return (
                  <option key={index} value={optionValue}>
                    {optionLabel}
                  </option>
                )
              })}
          </Form.Control>
        )

      case "checkbox":
        if (field.options && field.options.length > 0) {
          // Multiple checkboxes (checkbox group)
          const currentValue = Array.isArray(value) ? value : [] // Initialize as empty array if undefined
          return (
            <div>
              {field.options.map((option, index) => {
                const optionLabel = typeof option === "object" ? option.label : option
                const optionValue = typeof option === "object" ? option.value : option
                const isChecked = currentValue.includes(optionValue)

                return (
                  <Form.Check
                    key={index}
                    type="checkbox"
                    id={`${field.id}-checkbox-${index}`}
                    label={optionLabel}
                    value={optionValue}
                    checked={isChecked}
                    onChange={handleChange}
                    isInvalid={!!error}
                    required={field.required && index === 0} // Only mark first checkbox as required
                  />
                )
              })}
            </div>
          )
        } else {
          // Single checkbox
          return (
            <Form.Check
              type="checkbox"
              id={`${field.id}-checkbox`}
              label={field.checkboxLabel || field.label}
              checked={!!value} // Ensure boolean for single checkbox
              onChange={handleChange}
              isInvalid={!!error}
              required={field.required}
            />
          )
        }

      case "radio":
        return (
          <div>
            {field.options &&
              field.options.map((option, index) => {
                const optionLabel = typeof option === "object" ? option.label : option
                const optionValue = typeof option === "object" ? option.value : option

                return (
                  <Form.Check
                    key={index}
                    type="radio"
                    id={`${field.id}-radio-${index}`}
                    label={optionLabel}
                    name={field.id}
                    value={optionValue}
                    checked={value === optionValue}
                    onChange={handleChange}
                    isInvalid={!!error}
                    required={field.required && index === 0} // Only mark first radio as required
                  />
                )
              })}
          </div>
        )

      default:
        return (
          <Form.Control
            type="text"
            placeholder={field.placeholder}
            value={value || ""}
            onChange={handleChange}
            isInvalid={!!error}
            required={field.required}
          />
        )
    }
  }

  return (
    <Form.Group className="mb-3">
      {(field.type !== "checkbox" || field.options) && field.type !== "hidden" && (
        <Form.Label>
          {field.label}
          {field.required && <span className="text-danger">*</span>}
        </Form.Label>
      )}
      {renderField()}
      {field.helpText && !error && <Form.Text className="text-muted">{field.helpText}</Form.Text>}
      {error && <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>}
    </Form.Group>
  )
}
